import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import GatewayShell from "../../gateway/GatewayShell";
import GatewayAction from "../../gateway/GatewayAction";
import GatewayNotice from "../../gateway/GatewayNotice";
import GatewaySecurityMethods from "../../gateway/GatewaySecurityMethods";
import GatewayCodeInput from "../../gateway/GatewayCodeInput";
import GatewayField from "../../gateway/GatewayField";
import GatewayIdentityCard from "../../gateway/GatewayIdentityCard";
import { setCookie } from "../../utilities/cookies";
import { buildAuthenticatedDestination, clearRememberedRedirectUrl, resolveRedirectUrl } from "../../utilities/authRedirect";
import { getDestinationContext } from "../../gateway/destinationEngine";
import { useRedirectUrl } from "../../hooks/Authorize";
import { clearPendingAuthentication, getPendingAuthentication } from "../../gateway/pendingAuthentication";
import { clearIdentityTransaction, getIdentityTransaction, updateIdentityTransaction } from "../../gateway/identityTransaction";

export default function SecurityVerification() {
  const navigate = useNavigate();
  const redirectUrl = useRedirectUrl();
  const pending = useMemo(() => getPendingAuthentication(), []);
  const methods = pending?.security?.availableMethods || [];
  const capabilities = useMemo(() => ({
    methods: {
      authenticator: { enabled: methods.includes("authenticator"), available: methods.includes("authenticator") },
      backupCode: { enabled: methods.includes("backupCode"), available: methods.includes("backupCode") },
      emailCode: { enabled: methods.includes("emailCode"), available: methods.includes("emailCode") },
    },
  }), [methods]);
  const [selected, setSelected] = useState(methods[0] || "authenticator");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [attemptsRemaining, setAttemptsRemaining] = useState(null);
  const [emailDestination, setEmailDestination] = useState("");
  const [emailSending, setEmailSending] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(() => Number(pending?.security?.expiresInSeconds || 600));
  const requestInFlight = useRef(false);
  const transaction = getIdentityTransaction();
  const destination = getDestinationContext({ redirectUrl, referralApp: transaction?.referralApp || "" });

  useEffect(() => {
    if (!pending) navigate("/login", { replace: true });
  }, [navigate, pending]);

  useEffect(() => {
    if (!pending) return undefined;
    const expiresAt = Number(pending.storedAt || Date.now()) + Number(pending.security?.expiresInSeconds || 600) * 1000;
    const update = () => setSecondsRemaining(Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000)));
    update();
    const timer = window.setInterval(update, 1000);
    return () => window.clearInterval(timer);
  }, [pending]);

  if (!pending) return null;

  const sendEmailCode = async () => {
    if (!pending?.security?.challengeToken || emailSending) return;
    setEmailSending(true); setError("");
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/auth/security/challenge/email/send`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ challengeToken: pending.security.challengeToken }) });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.success) throw new Error(payload.message || "Email code could not be sent");
      setEmailDestination(payload.maskedDestination || "your recovery email");
      setCode("");
    } catch (sendError) { setError(sendError.message); }
    finally { setEmailSending(false); }
  };

  const submit = async (event) => {
    event.preventDefault();
    if (requestInFlight.current || submitting) return;
    if (secondsRemaining <= 0) {
      clearPendingAuthentication();
      setError("Your verification session expired. Sign in again to continue.");
      return;
    }
    if (code.length < 6) return setError("Enter the six-digit verification code");
    requestInFlight.current = true;
    setSubmitting(true);
    setError("");
    try {
      const response = await fetch(`${process.env.REACT_APP_BASE_URL}/api/auth/security/challenge/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ challengeToken: pending.security.challengeToken, code, method: selected }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || !payload.success) {
        const failure = new Error(payload.message || payload.error || "Security verification failed");
        failure.code = payload.code;
        failure.retryable = payload.retryable;
        failure.attemptsRemaining = payload.attemptsRemaining;
        throw failure;
      }
      setCookie("user", payload);
      clearPendingAuthentication();
      const postLoginPath = transaction?.internalReturnPath;
      const resolvedRedirect = resolveRedirectUrl(redirectUrl);
      clearRememberedRedirectUrl();
      if (postLoginPath?.startsWith("/")) {
        updateIdentityTransaction({ internalReturnPath: "" });
        navigate(postLoginPath, { replace: true });
        return;
      }
      if (!resolvedRedirect) {
        const authenticatedDestination = buildAuthenticatedDestination({ userId: payload.user?._id, refreshToken: payload.refreshToken, security: payload.security });
        clearIdentityTransaction();
        window.location.replace(authenticatedDestination);
        return;
      }
      const authenticatedDestination = buildAuthenticatedDestination({
        redirectUrl: resolvedRedirect,
        userId: payload.user?._id,
        refreshToken: payload.refreshToken,
        security: payload.security,
      });
      clearIdentityTransaction();
      window.location.replace(authenticatedDestination);
    } catch (verificationError) {
      const mustRestart = verificationError.code === "MFA_CHALLENGE_EXPIRED" || verificationError.code === "MFA_ATTEMPTS_EXHAUSTED" || verificationError.retryable === false;
      setError(verificationError.message);
      setAttemptsRemaining(Number.isFinite(verificationError.attemptsRemaining) ? verificationError.attemptsRemaining : null);
      setCode("");
      toast.error(verificationError.message);
      if (mustRestart) {
        clearPendingAuthentication();
      }
    } finally {
      requestInFlight.current = false;
      setSubmitting(false);
    }
  };

  return (
    <GatewayShell stage="security-verification" focused destinationLabel={destination.continuingLabel} onBack={() => navigate("/login_2")}>
      <form className="gatewayCard" onSubmit={submit} noValidate>
        <h2>Security verification</h2>
        <p className="gatewayCard__sub">Confirm it’s you with a method protected by your Clikkle identity.</p>
        <GatewayIdentityCard identity={pending.user} compact />
        <GatewaySecurityMethods capabilities={capabilities} selected={selected} onSelect={(method) => { setSelected(method); setCode(""); setError(""); if (method === "emailCode") setTimeout(sendEmailCode, 0); }} />
        {selected === "backupCode" ? (
          <GatewayField
            name="securityCode"
            label="Backup code"
            placeholder="XXXXX-XXXXX"
            value={code}
            onChange={(event) => setCode(event.target.value.toUpperCase().replace(/[^A-Z0-9-]/g, "").slice(0, 11))}
            error={error}
            autoFocus
          />
        ) : (
          <GatewayCodeInput
            name="securityCode"
            label={selected === "emailCode" ? "Email verification code" : "Authenticator code"}
            placeholder="000000"
            value={code}
            onChange={(event) => setCode(event.target.value)}
            error={error}
            autoFocus
          />
        )}
        <GatewayNotice title={selected === "backupCode" ? "Single-use backup code" : selected === "emailCode" ? "Recovery email verification" : "Authenticator protected"}>
          {selected === "backupCode" ? "Each backup code works once and is removed after use." : selected === "emailCode" ? `We sent a six-digit code to ${emailDestination || "your recovery email"}.` : "Enter the newest six-digit code from your authenticator app. Codes refresh every 30 seconds."}
        </GatewayNotice>
        {selected === "emailCode" ? <GatewayAction variant="secondary" type="button" disabled={emailSending} onClick={sendEmailCode}>{emailSending ? "Sending code…" : "Send another code"}</GatewayAction> : null}
        <p className="gatewayVerificationMeta" aria-live="polite">
          {secondsRemaining > 0 ? `Verification session: ${Math.floor(secondsRemaining / 60)}:${String(secondsRemaining % 60).padStart(2, "0")}` : "Verification session expired"}
          {attemptsRemaining !== null ? ` · ${attemptsRemaining} attempts remaining` : ""}
        </p>
        <GatewayAction type="submit" disabled={submitting || secondsRemaining <= 0}>{submitting ? "Verifying securely…" : "Verify and continue"}</GatewayAction>
        <GatewayAction variant="secondary" type="button" onClick={() => { clearPendingAuthentication(); navigate("/login"); }}>Use another account</GatewayAction>
      </form>
    </GatewayShell>
  );
}
